import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Video, Users, Headphones, Clock, BookOpen, Award } from 'lucide-react';
import CurrencyToggle from '../CurrencyToggle';

export const OrderSummary = ({
  bundle, selectedModality, onModalityChange, isConsultation,
  currency, formatBRL, installmentCount, monthlyValue, enrollmentFee, price,
}) => (
  <Card className="bg-zinc-900 border-zinc-800 sticky top-24">
    <CardHeader>
      <CardTitle className="text-white">Resumo</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <div className="text-sm text-gray-400">Formação</div>
        <div className="text-white font-semibold">{bundle?.title}</div>
      </div>

      {/* Seletor de Modalidade com Comparativo */}
      <div>
        <div className="text-sm text-gray-400 mb-2">Escolha sua Modalidade</div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button
            type="button"
            onClick={() => onModalityChange('ead_recorded')}
            className={`p-3 rounded-lg text-xs font-medium transition-all border-2 ${
              selectedModality === 'ead_recorded'
                ? 'bg-red-600/20 border-red-500 text-white'
                : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-zinc-600'
            }`}
          >
            <Video size={16} className="mx-auto mb-1" />
            EaD Gravado
          </button>
          <button
            type="button"
            onClick={() => onModalityChange('ead_live')}
            className={`p-3 rounded-lg text-xs font-medium transition-all border-2 ${
              selectedModality === 'ead_live'
                ? 'bg-purple-600/20 border-purple-500 text-white'
                : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-zinc-600'
            }`}
          >
            <Users size={16} className="mx-auto mb-1" />
            Elite
          </button>
          <button
            type="button"
            onClick={() => onModalityChange('presential')}
            className={`p-3 rounded-lg text-xs font-medium transition-all border-2 ${
              selectedModality === 'presential'
                ? 'bg-amber-600/20 border-amber-500 text-white'
                : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-zinc-600'
            }`}
          >
            <Headphones size={16} className="mx-auto mb-1" />
            Mentor
          </button>
        </div>

        {/* Comparativo Visual da Modalidade Selecionada */}
        <div className={`rounded-lg p-3 text-xs space-y-2 ${
          selectedModality === 'ead_recorded' ? 'bg-red-900/20 border border-red-500/30' :
          selectedModality === 'ead_live' ? 'bg-purple-900/20 border border-purple-500/30' :
          'bg-amber-900/20 border border-amber-500/30'
        }`}>
          <div className={`font-semibold ${
            selectedModality === 'ead_recorded' ? 'text-red-400' :
            selectedModality === 'ead_live' ? 'text-purple-400' :
            'text-amber-400'
          }`}>
            {selectedModality === 'ead_recorded' && '📹 EaD Gravado (On-demand)'}
            {selectedModality === 'ead_live' && '🎯 Elite (Ao Vivo)'}
            {selectedModality === 'presential' && '👨‍🏫 Mentor (Presencial)'}
          </div>

          <div className="space-y-1.5 text-gray-300">
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-green-400" />
              <span>
                {selectedModality === 'ead_recorded' && 'Acesso vitalício às aulas'}
                {selectedModality === 'ead_live' && 'Acesso por 12 meses + gravações'}
                {selectedModality === 'presential' && 'Acesso vitalício + aulas presenciais'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <BookOpen size={12} className="text-green-400" />
              <span>
                {selectedModality === 'ead_recorded' && 'Estude no seu próprio ritmo'}
                {selectedModality === 'ead_live' && 'Aulas ao vivo com interação'}
                {selectedModality === 'presential' && 'Imersão presencial intensiva'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {selectedModality === 'ead_recorded' ? (
                <>
                  <Headphones size={12} className="text-green-400" />
                  <span>Suporte por email</span>
                </>
              ) : selectedModality === 'ead_live' ? (
                <>
                  <Headphones size={12} className="text-green-400" />
                  <span>Tire dúvidas ao vivo com professor</span>
                </>
              ) : (
                <>
                  <Headphones size={12} className="text-green-400" />
                  <span>Mentoria individual incluída</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Award size={12} className="text-green-400" />
              <span>
                {selectedModality === 'ead_recorded' && 'Certificado digital'}
                {selectedModality === 'ead_live' && 'Certificado + Badge Elite'}
                {selectedModality === 'presential' && 'Certificado Premium + Networking'}
              </span>
            </div>

            {selectedModality !== 'ead_recorded' && (
              <div className="flex items-center gap-2">
                <Users size={12} className="text-green-400" />
                <span>
                  {selectedModality === 'ead_live' && 'Grupo exclusivo WhatsApp'}
                  {selectedModality === 'presential' && 'Grupo VIP + Acesso ao instrutor'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="text-sm text-gray-400">Carga Horária</div>
        <div className="text-white">{bundle?.total_hours}h</div>
      </div>
      <div className="border-t border-zinc-700 pt-4">
        {isConsultation ? (
          <div className="flex justify-between items-center text-lg font-bold">
            <span className="text-white">Investimento</span>
            <span className="text-red-500">Sob consulta</span>
          </div>
        ) : currency !== 'BRL' ? (
          <>
            <div className="flex justify-between items-center text-lg font-bold gap-2">
              <span className="text-white flex items-center gap-2">Valor total do curso <CurrencyToggle compact /></span>
              <span className="text-red-500">{formatBRL(price)}</span>
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-blue-300 bg-blue-500/10 border border-blue-400/30 rounded-full px-3 py-1" data-testid="eur-international-note">
              Pagamento internacional via PayPal
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400">Curso ({installmentCount}x)</span>
              <span className="text-white">{installmentCount}x de {formatBRL(monthlyValue)}</span>
            </div>
            <div className="flex justify-between items-center mb-2 gap-2">
              <span className="text-gray-400 flex items-center gap-2">Valor total do curso <CurrencyToggle compact /></span>
              <span className="text-white">{formatBRL(price)}</span>
            </div>
            <div className="flex justify-between items-center text-lg font-bold border-t border-zinc-700 pt-3 mt-2">
              <span className="text-white">Matrícula (pague agora)</span>
              <span className="text-red-500">{formatBRL(enrollmentFee)}</span>
            </div>
          </>
        )}
      </div>

      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
        <div className="text-sm text-green-400 font-semibold mb-2">✓ Incluso na matrícula:</div>
        <ul className="text-xs text-gray-300 space-y-1">
          <li>• Email de confirmação</li>
          <li>• Calendário completo das aulas</li>
          <li>• Acesso ao grupo WhatsApp</li>
          <li>• Certificado ao final</li>
        </ul>
      </div>
    </CardContent>
  </Card>
);

export default OrderSummary;
